/**
 * Next action handlers for processor-specific user actions (e.g., 3DS authentication).
 *
 * This module abstracts processor-specific logic away from the generic card payment flow.
 * Each handler implements the logic needed for a specific next action type.
 */

import type { PublicCardCheckoutResponse } from "@pkg/sdk/models";
import type { Stripe } from "@stripe/stripe-js";
import show3DSIframeModal from "../utils/3ds-iframe-modal";

// Helper to wait for a condition
const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

// Check if Stripe.js is loaded via script tag
const isStripeJsPresent = (): boolean => "Stripe" in window;

/**
 * Get the Stripe instance from the global window object.
 * Expects Stripe.js to be loaded via HTML script tag.
 * Polls for up to 5 seconds waiting for Stripe to be available.
 */
const getLoadedStripe = async (publishableKey: string): Promise<Stripe> => {
  // Poll for Stripe.js to be loaded (up to 5 seconds)
  for (let i = 0; i < 10; i++) {
    if (isStripeJsPresent()) {
      break;
    }
    await sleep(500);
  }

  if (!isStripeJsPresent()) {
    throw new Error(
      "Stripe.js not loaded. Add this script tag to your HTML <head>:\n" +
        '<script src="https://js.stripe.com/v3/"></script>',
    );
  }

  // @ts-expect-error Stripe is loaded globally via script tag
  const stripe: Stripe = new window.Stripe(publishableKey);
  return stripe;
};

/**
 * Result of handling a next action.
 */
export type NextActionResult = { success: true } | { success: false; error: string };

/**
 * Handle Stripe 3DS authentication.
 */
const handleStripe3ds = async (
  nextAction: Extract<PublicCardCheckoutResponse["nextAction"], { type: "stripe_3ds" }>,
): Promise<NextActionResult> => {
  const { clientSecret, stripePk } = nextAction;

  const stripe = await getLoadedStripe(stripePk);

  // Show 3DS modal
  const { error: stripeError } = await stripe.confirmCardPayment(clientSecret);

  if (stripeError) {
    console.error("[3DS:Stripe] Authentication failed:", stripeError.message);
    return { success: false, error: stripeError.message || "3DS authentication failed" };
  }

  return { success: true };
};

/**
 * Handle Airwallex 3DS authentication via iframe.
 *
 * Renders an iframe with the 3DS authentication URL. The iframe will redirect
 * to the 3DS callback page which sends a postMessage back to complete the flow.
 */
const handleAirwallex3ds = async (
  nextAction: Extract<PublicCardCheckoutResponse["nextAction"], { type: "airwallex_3ds" }>,
): Promise<NextActionResult> => {
  return show3DSIframeModal({
    url: nextAction.url,
    size: { width: 605, height: 550 },
    onMessage: (event) => {
      if (event.data.type !== "3ds_complete") {
        return { handled: false };
      }

      const { isSuccess, error } = event.data;

      if (isSuccess) {
        return { handled: true, result: { success: true, data: {} } };
      } else {
        console.error("[3DS:Airwallex] Authentication failed:", error);
        return {
          handled: true,
          result: { success: false, error: error || "3DS authentication failed" },
        };
      }
    },
  });
};

/**
 * Handle a next action based on its type.
 * Routes to the appropriate processor-specific handler.
 *
 * @param nextAction - The next action from the checkout response
 * @returns Result indicating success or failure with error message
 */
export const handleNextAction = async (
  nextAction: NonNullable<PublicCardCheckoutResponse["nextAction"]>,
): Promise<NextActionResult> => {
  switch (nextAction.type) {
    case "stripe_3ds":
      return handleStripe3ds(nextAction);
    case "airwallex_3ds":
      return handleAirwallex3ds(nextAction);
    default:
      // TypeScript will catch if we miss a case when new action types are added
      return { success: false, error: `Unknown next action type: ${(nextAction as { type: string }).type}` };
  }
};
